import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { usePageTitle } from "../hooks/usePageTitle";
import { playAlert, playError, warmUp as warmUpAudio, startKeepAlive, stopKeepAlive } from "../components/helpers/audioPlayer";
import { ScanBarcode, Package, Hand, Flag, Minus, Plus, Printer } from "lucide-react";
import { useTranslation } from "../i18n";
import type { DeskInfo, DispatchOrder, DispatchBox, CourierBookingResult, DispatchOrderItem, FlaggedPackState, DeskPackState, DeskClaim } from "../types/Dispatch";

import DispatchScanDesk from "../components/dispatch/dispatch-scan-desk";
import { scannableBarcodes } from "../components/dispatch/itemBarcodes";
import { logDispatchEvent } from "../components/dispatch/dispatchLog";
import { type ReopenResult } from "../components/dispatch/pack-state";
import DispatchScanTote, { type ResolvedUnit } from "../components/dispatch/dispatch-scan-tote";
import * as deskApi from "../components/dispatch/deskSessionApi";
import { createPackStateSaver, type ServerSession } from "../components/dispatch/deskSessionApi";
import {
    freshOrderState, buildPackState, restoreOrderState, buildRestoredDesk, packStateKey, boxSignature,
    claimNotice, allMissingPackState, type ToteSession,
} from "../components/dispatch/deskOrderState";
import FlowLoader from "../components/FlowLoader";
import DispatchScanItem from "../components/dispatch/dispatch-scan-item";
import DispatchFlagRemaining from "../components/dispatch/dispatch-flag-remaining";
import DispatchOrderPack from "../components/dispatch/dispatch-order-pack";
import DispatchCloseOrder from "../components/dispatch/dispatch-close-order";
import DispatchTimeBonus from "../components/dispatch/dispatch-stats-bar";
import { DispatchSessionProvider } from "../components/dispatch/dispatch-session-chrome";
import DispatchOrderBar from "../components/dispatch/dispatch-order-bar";
import FlaggedPickNotice, { flaggedPickItems, AWAITING_FLAGGED_PICK_REASON } from "../components/dispatch/flagged-pick-notice";
import DsdOrderNotice, { isDsdOrder, hasUnitLeft } from "../components/dispatch/dsd-order-notice";
import OrderNotesNotice from "../components/dispatch/order-notes-notice";
import { fetchPackerNotes, type PackerNote } from "../components/dispatch/packer-notes";
import { useCycleScore, type PointsTargetKey } from "../components/gamification";
import { getAutoPrint, setAutoPrintStorage, fetchTodayStats as fetchTodayStatsShared } from "../components/dispatch/dispatchSession";
import type { DispatchStats } from "../components/dispatch/dispatchSession";
import { silentPrint, browserPrint, buildFlaggedLabels, getSavedLocalPrinter, getPrintModePreference, resolvePrintAgentDownloadUrl } from "../components/helpers/printAgent";
import { submitNetworkPrintJob, getSavedNetworkPrinter, detectPrintMode } from "../components/helpers/networkPrint";
import { toast } from "react-toastify";

import { apiFetch } from "../api/apiFetch";
import { summariseCarrierVoids, carrierVoidFailureDetail, carrierReissueDetail, type CarrierVoidResponse } from "../components/helpers/carrierVoidResult";
import "../styles/dispatch.css";
import "../styles/scansheet.css";
import "../styles/flagged-picks.css";
import "../styles/dispatch-bench.css";

type DispatchView = "scan-desk" | "scan-tote" | "scan-item" | "order-pack" | "confirm-ship" | "flag-confirm" | "flag-remaining" | "flagged" | "flagged-pick-notice" | "dsd-notice" | "order-notes-notice";

const getSessionTote = (): ToteSession | null => {
    try {
        const stored = window.sessionStorage.getItem(`dispatch-tote-${getUserId()}`);
        return stored ? JSON.parse(stored) : null;
    } catch { return null; }
};
const setSessionTote = (tote: ToteSession) => {
    window.sessionStorage.setItem(`dispatch-tote-${getUserId()}`, JSON.stringify(tote));
};
const clearSessionTote = () => {
    window.sessionStorage.removeItem(`dispatch-tote-${getUserId()}`);
};

// Persist desk info per user session
const getSessionDesk = (): DeskInfo | null => {
    try {
        const stored = window.sessionStorage.getItem(`dispatch-desk-${getUserId()}`);
        return stored ? JSON.parse(stored) : null;
    } catch { return null; }
};

const setSessionDesk = (desk: DeskInfo) => {
    window.sessionStorage.setItem(`dispatch-desk-${getUserId()}`, JSON.stringify(desk));
};

const clearSessionDesk = () => {
    window.sessionStorage.removeItem(`dispatch-desk-${getUserId()}`);
};

// The desk, the tote and the open order with its pack state are saved on the
// ACCOUNT (src/components/dispatch/deskSessionApi.ts) so any tab, any PC and
// any logout resumes where the packer left off. These sessionStorage helpers
// are a write-through MIRROR of that: read only when the server cannot be
// asked (network, 5xx, 404 on an old BE, 503 desk_session_unavailable).
//
// Persist open order so user must complete it even if they navigate away
const getSessionOrder = (): { order: DispatchOrder; boxes: DispatchBox[]; view: DispatchView; activeBox?: number } | null => {
    try {
        const stored = window.sessionStorage.getItem(`dispatch-order-${getUserId()}`);
        if (!stored) return null;
        const parsed = JSON.parse(stored);
        // Migrate stale data: assignedBox (number) → assignedBoxes (array) and
        // backfill the per-status counts introduced later (missing/damaged/barcode-damaged).
        if (parsed?.order?.items) {
            parsed.order.items = parsed.order.items.map((item: any) => {
                const next = { ...item };
                if (!Array.isArray(next.assignedBoxes)) {
                    next.assignedBoxes = item.assignedBox ? [item.assignedBox] : [];
                }
                if (typeof next.scannedCount !== "number") next.scannedCount = 0;
                if (typeof next.missingCount !== "number") next.missingCount = 0;
                if (typeof next.damagedCount !== "number") next.damagedCount = 0;
                if (typeof next.wrongItemCount !== "number") next.wrongItemCount = 0;
                if (typeof next.barcodeDamagedCount !== "number") next.barcodeDamagedCount = 0;
                return next;
            });
        }
        return parsed;
    } catch { return null; }
};

const setSessionOrder = (order: DispatchOrder, boxes: DispatchBox[], view: DispatchView, activeBox?: number) => {
    window.sessionStorage.setItem(`dispatch-order-${getUserId()}`, JSON.stringify({ order, boxes, view, activeBox }));
};

const clearSessionOrder = () => {
    window.sessionStorage.removeItem(`dispatch-order-${getUserId()}`);
};

// Session stats — persisted per user in sessionStorage
const getUserId = (): string => {
    try {
        const userData = window.localStorage.getItem("user");
        return userData ? JSON.parse(userData).uid || "unknown" : "unknown";
    } catch { return "unknown"; }
};

// The signed-in user's site. Points targets are per warehouse, and the desk
// session doesn't carry one until a tote is scanned.
const getUserWarehouse = (): string | undefined => {
    try {
        const userData = window.localStorage.getItem("user");
        if (!userData) return undefined;
        const parsed = JSON.parse(userData);
        return typeof parsed?.warehouse === "string" ? parsed.warehouse : undefined;
    } catch { return undefined; }
};

// Display name of the currently-logged-in user — used to stamp flagged orders
// so admins can see who raised them.
const getCurrentUserName = (): string => {
    try {
        const userData = window.localStorage.getItem("user");
        if (!userData) return "";
        const parsed = JSON.parse(userData);
        return parsed.name || "";
    } catch { return ""; }
};

// Stats are persisted server-side per (user, day) — table row materialises
// on first bump and a new day implicitly starts with zeros via the
// (user_id, day) PK. No localStorage / sessionStorage cache: a fetch on
// mount restores today's totals, and bumps fire-and-forget so the UI is
// optimistic but the DB is the source of truth.
const fetchTodayStats = () => fetchTodayStatsShared(apiFetch);

const bumpStatsRemote = (delta: { items?: number; orders?: number }) => {
    try {
        const userData = window.localStorage.getItem("user");
        const uid = userData ? JSON.parse(userData).uid : null;
        if (uid == null) return;
        // Fire-and-forget — don't block the scan UX on the round trip. If
        // the request drops, the next page load's fetchTodayStats will
        // resync state to whatever did land server-side.
        apiFetch(`/dispatch-admin/stats/bump`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ uid, ...delta }),
        }).catch(() => { /* tracking-only, no UX surface */ });
    } catch { /* ignore */ }
};


// Last shipped order — for reopening, per user
const getLastShippedOrder = (): { order: DispatchOrder; boxes: DispatchBox[] } | null => {
    try {
        const stored = window.sessionStorage.getItem(`dispatch-last-order-${getUserId()}`);
        if (!stored) return null;
        const parsed = JSON.parse(stored);
        // Migrate old format (just order) to new format (order + boxes)
        const wrapped = parsed.order_id ? { order: parsed, boxes: [{ id: 1, items: [], boxRef: null }] } : parsed;
        // Backfill count fields added later so reopened orders don't produce NaN.
        if (wrapped?.order?.items) {
            wrapped.order.items = wrapped.order.items.map((item: any) => ({
                ...item,
                assignedBoxes: Array.isArray(item.assignedBoxes) ? item.assignedBoxes : (item.assignedBox ? [item.assignedBox] : []),
                scannedCount: typeof item.scannedCount === "number" ? item.scannedCount : 0,
                missingCount: typeof item.missingCount === "number" ? item.missingCount : 0,
                damagedCount: typeof item.damagedCount === "number" ? item.damagedCount : 0,
                wrongItemCount: typeof item.wrongItemCount === "number" ? item.wrongItemCount : 0,
                barcodeDamagedCount: typeof item.barcodeDamagedCount === "number" ? item.barcodeDamagedCount : 0,
            }));
        }
        return wrapped;
    } catch { return null; }
};

const saveLastShippedOrder = (order: DispatchOrder, boxes: DispatchBox[]) => {
    window.sessionStorage.setItem(`dispatch-last-order-${getUserId()}`, JSON.stringify({ order, boxes }));
};

const IDLE_TIMEOUT_MS = 180_000;

// Never let a hung network hold up Put back / Flag / Ship.
const withTimeout = (p: Promise<unknown>, ms: number): Promise<void> =>
    Promise.race([p, new Promise(resolve => setTimeout(resolve, ms))]).then(() => undefined);

const SYNCABLE_VIEWS: DispatchView[] = ["scan-desk", "scan-tote", "scan-item", "order-pack"];

const Dispatch = () => {
    usePageTitle("dispatch");
    const navigate = useNavigate();

    // Pre-render all audio buffers and keep AudioContext alive
    useEffect(() => {
        warmUpAudio();
        startKeepAlive();
        return () => stopKeepAlive();
    }, []);

    const { t } = useTranslation();

    // Prompt to install the print agent ONLY when this device has no print
    // route at all — no local agent AND no usable network printer. Machines
    // that print via a network printer are fully set up and must not be
    // nagged. Outdated agents are not prompted either: agents ≥ 1.4.0
    // self-update from the BE, and operators can't run the installer anyway
    // (it needs admin) — the printer modal + admin Printers page carry the
    // passive version hints for IT. Desktop only — mobile can't run the agent.
    useEffect(() => {
        if (/Mobi|Android|iPhone|iPad/i.test(navigator.userAgent)) return;
        detectPrintMode().then(async mode => {
            if (mode !== null) return;
            const downloadUrl = await resolvePrintAgentDownloadUrl();
            toast.dismiss();
            toast.warn(
                <div>
                    {t("print_agent_not_installed") || "Print Agent not installed"}
                    <br />
                    <a
                        href={downloadUrl}
                        style={{ color: "white", textDecoration: "underline", fontWeight: 600 }}
                    >
                        {t("download_print_agent") || "Download Print Agent"}
                    </a>
                </div>,
                { autoClose: 15000 }
            );
        });
    }, [t]);
    // Desk, tote and open order come from GET /dispatch/session (see the load
    // effect below); until it answers the page shows a loader. sessionStorage
    // is only read when that call fails.
    const [sessionReady, setSessionReady] = useState(false);
    const [view, setView] = useState<DispatchView>("scan-desk");
    const [deskInfo, setDeskInfo] = useState<DeskInfo | null>(null);
    const [toteSession, setToteSession] = useState<ToteSession | null>(null);
    const [currentOrder, setCurrentOrder] = useState<DispatchOrder | null>(null);
    const [boxes, setBoxes] = useState<DispatchBox[]>([]);
    // The box tab the packer is filling — kept here so it is saved with the
    // pack state and restored (the pack screen owns the live value).
    const [activeBox, setActiveBox] = useState<number | undefined>(undefined);
    // Notes an admin left on the open order (packer-notes.tsx), fetched as
    // it opens; any at all puts the order-notes notice in front of packing.
    const [packerNotes, setPackerNotes] = useState<PackerNote[]>([]);
    const [courierResult, setCourierResult] = useState<CourierBookingResult | null>(null);
    // The carrier label booked BEFORE the packer came back from Confirm
    // Shipment to change the boxes. The pack screen warns when the bench
    // holds a different number of boxes than this label covers, and
    // Complete then voids and re-books. Cleared wherever the order is
    // finished, flagged or abandoned (the same places courierResult is).
    const [priorBooking, setPriorBooking] = useState<{ id: number; pieces: number; carrierName?: string | null } | null>(null);
    const [returnedFromShip, setReturnedFromShip] = useState(false);
    const [stats, setStats] = useState<DispatchStats>({ itemsScanned: 0, ordersShipped: 0 });
    // Pull today's totals from the BE on mount so a fresh session shows
    // the operator's running counts rather than zeros.
    useEffect(() => {
        let alive = true;
        fetchTodayStats().then(s => { if (alive) setStats(s); });
        return () => { alive = false; };
    }, []);
    const [showIdleAlert, setShowIdleAlert] = useState(false);
    const [autoPrint, setAutoPrint] = useState(getAutoPrint());
    const savedLastOrder = getLastShippedOrder();
    const [lastOrder, setLastOrder] = useState<{ order: DispatchOrder; boxes: DispatchBox[] } | null>(savedLastOrder);

    // Flag-confirm view state: the reason to stamp and the label count
    const [pendingFlagReason, setPendingFlagReason] = useState("");
    const [flagLabelCount, setFlagLabelCount] = useState(1);
    const [flagPrinting, setFlagPrinting] = useState(false);
    const [flagReturnView, setFlagReturnView] = useState<DispatchView>("order-pack");
    // Flag missing (Singles tote): the flags are in flight; how many the
    // "flagged" overlay reports.
    const [flagRemainingBusy, setFlagRemainingBusy] = useState(false);
    const [flaggedCount, setFlaggedCount] = useState(1);

    const toggleAutoPrint = useCallback(() => {
        setAutoPrint(prev => {
            const next = !prev;
            setAutoPrintStorage(next);
            return next;
        });
    }, []);
    const idleTimerRef = useRef<NodeJS.Timeout | null>(null);

    // Trophy points. The unit of work is one order: opened at the bench,
    // closed when its labels are booked. The target follows the pick type the
    // unit came from — a Large order and a Single are not the same job, and
    // one target for both would make one of them meaningless.
    //
    // A reopened shipping-label scan has no pick type and deliberately doesn't
    // score: it's an exception path, not a packing cycle.
    const dispatchTargetKey: PointsTargetKey | null =
        toteSession?.unitType === "large_tote" ? "dispatch_timer_large_ms"
        : toteSession?.unitType === "pas_location" ? "dispatch_timer_pick_and_sort_ms"
        // A Sorter 2 tote is one sorted order in a tote — the same job as
        // a P&S dispatch tote, so it scores against the same target.
        : toteSession?.unitType === "zone_tote" ? "dispatch_timer_pick_and_sort_ms"
        : toteSession?.unitType === "light_tote" ? "dispatch_timer_light_ms"
        : null;
    const userWarehouse = useMemo(getUserWarehouse, []);
    const cycle = useCycleScore(userWarehouse, dispatchTargetKey, "dispatch-best-time");
    const cycleRef = useRef(cycle);
    cycleRef.current = cycle;

    // Bump item count — optimistic local update + fire-and-forget POST
    // so the BE row stays in sync. fetchTodayStats on next mount will
    // reconcile if anything dropped.
    const bumpItemsScanned = useCallback((count: number = 1) => {
        setStats(prev => ({ ...prev, itemsScanned: prev.itemsScanned + count }));
        bumpStatsRemote({ items: count });
    }, []);

    // Bump order count
    const bumpOrdersShipped = useCallback(() => {
        setStats(prev => ({ ...prev, ordersShipped: prev.ordersShipped + 1 }));
        bumpStatsRemote({ orders: 1 });
    }, []);

    // Reset idle timer on any activity
    const resetIdleTimer = useCallback(() => {
        setShowIdleAlert(false);
        if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
        // Only run idle timer on active scan/pack screens
        if (view === "scan-item" || view === "order-pack") {
            idleTimerRef.current = setTimeout(() => {
                playAlert();
                setShowIdleAlert(true);
            }, IDLE_TIMEOUT_MS);
        }
    }, [view]);

    // Start/restart idle timer on view change and any user interaction
    useEffect(() => {
        resetIdleTimer();

        const onActivity = () => resetIdleTimer();
        window.addEventListener("keydown", onActivity);
        window.addEventListener("click", onActivity);
        window.addEventListener("touchstart", onActivity);

        return () => {
            if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
            window.removeEventListener("keydown", onActivity);
            window.removeEventListener("click", onActivity);
            window.removeEventListener("touchstart", onActivity);
        };
    }, [resetIdleTimer]);

    // ── Desk session on the account ───────────────────────────────
    const serverOnRef = useRef(false);            // the server answered the first GET
    const sessionReadyRef = useRef(false);
    const loadStartedRef = useRef(false);
    const deskRef = useRef<DeskInfo | null>(null);
    const toteRef = useRef<ToteSession | null>(null);
    const orderRef = useRef<DispatchOrder | null>(null);
    const viewRef = useRef<DispatchView>("scan-desk");
    const openingRef = useRef(false);
    const leavingRef = useRef(false);
    const lastScheduledRef = useRef<string | null>(null);
    const lastBoxSigRef = useRef("");
    const flushNextRef = useRef(false);
    viewRef.current = view;
    orderRef.current = currentOrder;

    const updateDesk = (next: DeskInfo | null) => { deskRef.current = next; setDeskInfo(next); };
    const updateTote = (next: ToteSession | null) => { toteRef.current = next; setToteSession(next); };
    const applyOrder = (next: { order: DispatchOrder; boxes: DispatchBox[]; activeBox?: number } | null) => {
        orderRef.current = next?.order ?? null;
        setCurrentOrder(next?.order ?? null);
        setBoxes(next?.boxes ?? []);
        setActiveBox(next?.activeBox);
    };

    // Save queue for PUT /dispatch/session/pack-state. The callbacks read the
    // latest handlers through a ref because the queue lives for the page's life.
    const handlersRef = useRef<{
        versionConflict: (e: { orderId: string; packState: DeskPackState | null; version: number }) => void;
        claimLost: (e: { orderId: string; claim: DeskClaim | null }) => void;
    }>({ versionConflict: () => {}, claimLost: () => {} });
    const saverRef = useRef<ReturnType<typeof createPackStateSaver> | null>(null);
    if (!saverRef.current) {
        saverRef.current = createPackStateSaver({
            onVersionConflict: e => handlersRef.current.versionConflict(e),
            onClaimLost: e => handlersRef.current.claimLost(e),
            onUnavailable: () => { serverOnRef.current = false; },
        });
    }
    const saver = saverRef.current;
    const flushNow = useCallback(() => withTimeout(saverRef.current!.flush(), 4000), []);

    // Another tab saved a newer pack state: show it, say so.
    handlersRef.current.versionConflict = ({ orderId, packState, version }) => {
        const base = orderRef.current;
        if (!base || base.order_id !== orderId) return;
        const restored = restoreOrderState(base, packState);
        if (!restored) return;
        saverRef.current!.setVersion(version);
        applyOrder(restored);
        lastScheduledRef.current = packStateKey(buildPackState(restored.order, restored.boxes, restored.activeBox), viewRef.current);
        toast.info(t("desk_pack_state_changed_elsewhere"), { toastId: "desk-version-conflict" });
    };

    // A team leader released the order under the packer: say so and send the
    // desk back to the tote scan (the boxes and scans are kept server-side).
    handlersRef.current.claimLost = ({ orderId }) => {
        if (!orderRef.current || orderRef.current.order_id !== orderId) return;
        playError();
        toast.dismiss();
        toast.warn(t("desk_claim_lost"), { toastId: "desk-claim-lost", autoClose: 15000 });
        applyOrder(null);
        clearSessionOrder();
        lastScheduledRef.current = null;
        setCourierResult(null);
        setPriorBooking(null);
        updateTote(null);
        setView("scan-tote");
        void deskApi.putSession({ unit: null });
    };

    const showClaimedNotice = (claim: DeskClaim | null | undefined) => {
        playError();
        toast.dismiss();
        toast.error(claimNotice(t, claim), { toastId: "desk-order-claimed", autoClose: 15000 });
    };

    const showReleasedNotice = (status?: string) => {
        toast.warn(
            status
                ? t("desk_order_released_not_dispatch", { status: t(`status_${status}`) })
                : t("desk_order_released_generic"),
            { toastId: "desk-order-released", autoClose: 15000 },
        );
    };

    // Apply the account's desk session to the page: desk, tote, open order
    // with its pack state, and which screen that means.
    const applyServerSession = (s: ServerSession) => {
        const r = buildRestoredDesk(s);
        updateDesk(r.desk);
        updateTote(r.tote);
        if (r.order) {
            applyOrder(r.order);
            saverRef.current!.begin(r.order.order.order_id, s.version ?? 0);
            lastScheduledRef.current = packStateKey(buildPackState(r.order.order, r.order.boxes, r.order.activeBox), "order-pack");
            lastBoxSigRef.current = boxSignature(r.order.boxes);
            setReturnedFromShip(true);
        } else {
            applyOrder(null);
            saverRef.current!.cancel();
            clearSessionOrder();
            lastScheduledRef.current = null;
        }
        setCourierResult(null);
        setPriorBooking(null);
        setView(r.view);
        if (s.released) showReleasedNotice(s.released.status);
    };

    const resyncFromServer = async (): Promise<boolean> => {
        const res = await deskApi.getSession();
        if (!res.ok || !res.data.session) return false;
        applyServerSession(res.data.session);
        return true;
    };

    // PUT /dispatch/session. False = the server refused because this
    // account already holds an order (another tab opened one): the server's
    // state has been applied instead and the caller must stop.
    const syncSession = async (patch: deskApi.SessionPatch): Promise<boolean> => {
        if (!serverOnRef.current) return true;
        const r = await deskApi.putSession(patch);
        if (r.ok) return true;
        if (r.kind === "conflict" && r.code === "order_open") {
            await resyncFromServer();
            toast.info(t("desk_order_already_open"), { toastId: "desk-order-open" });
            return false;
        }
        return true; // unavailable / rejected: carry on locally, the mirror has it
    };
    const syncUnit = (unit: ToteSession | null) => {
        if (serverOnRef.current) void deskApi.putSession({ unit });
    };

    type ClaimOutcome =
        | { kind: "bound"; packState: DeskPackState | null; version: number }
        | { kind: "local" }                       // server sessions off: carry on without a claim
        | { kind: "blocked"; notDispatch?: boolean }
        | { kind: "resynced" };

    // POST /dispatch/session/open-order: claim the order and learn its saved
    // progress. Says why (notice + sound) when the order cannot be opened.
    const claimOrderOnServer = async (order: DispatchOrder, packState?: DeskPackState | null): Promise<ClaimOutcome> => {
        if (!serverOnRef.current) return { kind: "local" };
        const r = await deskApi.openOrder(order.order_id, packState);
        if (r.ok) return { kind: "bound", packState: r.data.pack_state ?? null, version: r.data.version ?? 0 };
        if (r.kind === "unavailable") return { kind: "local" };
        if (r.kind === "conflict") {
            if (r.code === "order_claimed") {
                showClaimedNotice(r.body.claim as DeskClaim | undefined);
                return { kind: "blocked" };
            }
            if (r.code === "order_not_dispatch") {
                playError();
                toast.dismiss();
                toast.error(t("dispatch_label_order_not_dispatchable", {
                    order: order.order_number ?? "",
                    status: t(`status_${r.body.status}`),
                }), { toastId: "desk-order-not-dispatch" });
                return { kind: "blocked", notDispatch: true };
            }
            if (r.code === "order_open") {
                await resyncFromServer();
                toast.info(t("desk_order_already_open"), { toastId: "desk-order-open" });
                return { kind: "resynced" };
            }
        }
        if (r.kind !== "unauthorized") {
            playError();
            toast.dismiss();
            toast.error(t("server_error"), { toastId: "desk-open-failed" });
        }
        return { kind: "blocked" };
    };

    // First load: ask the account. Falls back to the sessionStorage mirror
    // when the server cannot answer, and uploads a mirror-only order once.
    useEffect(() => {
        if (loadStartedRef.current) return;
        loadStartedRef.current = true;
        (async () => {
            const mDesk = getSessionDesk();
            const mTote = getSessionTote();
            const mOrder = getSessionOrder();
            const res = await deskApi.getSession();
            if (!res.ok) {
                serverOnRef.current = false;
                if (mDesk) {
                    updateDesk(mDesk);
                    updateTote(mTote);
                    if (mOrder) {
                        applyOrder({ order: mOrder.order, boxes: mOrder.boxes, activeBox: mOrder.activeBox });
                        setReturnedFromShip(true);
                        // The notes aren't kept locally, so their notice can't come back.
                        setView(mOrder.view === "confirm-ship" || mOrder.view === "order-notes-notice" ? "order-pack" : mOrder.view);
                    } else {
                        setView(mTote ? "scan-item" : "scan-tote");
                    }
                }
            } else {
                serverOnRef.current = true;
                const session = res.data.session ?? null;
                if (session) {
                    applyServerSession(session);
                } else if (mDesk) {
                    // First use after this shipped: adopt the tab's desk and tote onto the account.
                    updateDesk(mDesk);
                    updateTote(mTote);
                    setView(mTote ? "scan-item" : "scan-tote");
                    await deskApi.putSession({ desk_id: mDesk.deskId, warehouse: mDesk.warehouse, unit: mTote ?? null });
                }
                if (!session?.order && mOrder && deskRef.current) {
                    // One-time upload: nothing in flight is lost at release.
                    const out = await claimOrderOnServer(mOrder.order, buildPackState(mOrder.order, mOrder.boxes, mOrder.activeBox));
                    if (out.kind === "blocked") {
                        clearSessionOrder();
                        setView(toteRef.current ? "scan-item" : "scan-tote");
                    } else if (out.kind === "bound" || out.kind === "local") {
                        const restored = out.kind === "bound"
                            ? restoreOrderState(mOrder.order, out.packState)
                            : null;
                        const next = restored ?? { order: mOrder.order, boxes: mOrder.boxes, activeBox: mOrder.activeBox };
                        applyOrder(next);
                        if (out.kind === "bound") {
                            saverRef.current!.begin(mOrder.order.order_id, out.version);
                            lastScheduledRef.current = packStateKey(buildPackState(next.order, next.boxes, next.activeBox), "order-pack");
                        }
                        setReturnedFromShip(true);
                        setView("order-pack");
                    }
                } else if (!session?.order) {
                    clearSessionOrder();
                }
            }
            sessionReadyRef.current = true;
            setSessionReady(true);
        })();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Mirror writes (only once the first load has settled, so the empty
    // initial state never wipes what a failed GET would still need).
    useEffect(() => {
        if (!sessionReady) return;
        if (deskInfo) setSessionDesk(deskInfo); else clearSessionDesk();
    }, [deskInfo, sessionReady]);
    useEffect(() => {
        if (!sessionReady) return;
        if (toteSession) setSessionTote(toteSession); else clearSessionTote();
    }, [toteSession, sessionReady]);

    // Every desk change saves the pack state (debounced ~800 ms, one request
    // in flight); a box scan / new box / removed box and Reset order go out
    // at once. The order mirror is written through here too.
    useEffect(() => {
        if (!sessionReady || !currentOrder) return;
        if (view !== "order-pack" && view !== "confirm-ship" && view !== "flagged-pick-notice" && view !== "dsd-notice" && view !== "order-notes-notice") return;
        setSessionOrder(currentOrder, boxes, view, activeBox);
        if (!saver.isBound()) return;
        const ps = buildPackState(currentOrder, boxes, activeBox);
        const key = packStateKey(ps, view);
        if (key === lastScheduledRef.current) return;
        lastScheduledRef.current = key;
        saver.schedule(ps, view);
        const sig = boxSignature(boxes);
        const boxChanged = sig !== lastBoxSigRef.current;
        lastBoxSigRef.current = sig;
        if (flushNextRef.current || boxChanged) {
            flushNextRef.current = false;
            void saver.flush();
        }
    }, [currentOrder, boxes, view, activeBox, sessionReady, saver]);

    // Backgrounded / closed: get the saved desk up to date. Foregrounded: a
    // stale second tab catches up before it is used.
    useEffect(() => {
        const onVisibility = async () => {
            if (document.visibilityState === "hidden") { void saverRef.current?.flush(); return; }
            if (!serverOnRef.current || !sessionReadyRef.current) return;
            if (!SYNCABLE_VIEWS.includes(viewRef.current)) return;
            await withTimeout(saverRef.current!.flush(), 4000);
            const res = await deskApi.getSession();
            if (!res.ok || !res.data.session) return;
            const s = res.data.session;
            if (!SYNCABLE_VIEWS.includes(viewRef.current)) return;
            const localOrderId = orderRef.current?.order_id ?? null;
            const serverOrderId = s.order?.order_id ?? null;
            const newer = serverOrderId !== null && serverOrderId === localOrderId
                && (s.version ?? 0) > saverRef.current!.getVersion();
            const deskDiffers = !localOrderId && (s.desk_id ?? null) !== (deskRef.current?.deskId ?? null);
            if (serverOrderId !== localOrderId || newer || deskDiffers || (s.released && localOrderId)) {
                applyServerSession(s);
            }
        };
        document.addEventListener("visibilitychange", onVisibility);
        return () => {
            document.removeEventListener("visibilitychange", onVisibility);
            void saverRef.current?.flush();
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleDeskScanned = async (desk: DeskInfo) => {
        updateDesk(desk);
        setView("scan-tote");
        // A new desk starts with no tote.
        if (!(await syncSession({ desk_id: desk.deskId, warehouse: desk.warehouse, unit: null }))) return;
        if (toteRef.current) updateTote(null);
    };

    // A tote / sorted bay location was scanned — hold its orders and move on.
    // Large totes and P&S bay slots carry exactly ONE order, so the order
    // opens immediately (an item scan would only re-identify what the tote
    // scan already identified). Single (light) totes comingle several
    // orders' items, so the item scan is what picks the order — keep it.
    // The length===1 guard is a safety net: any multi-order unit falls back
    // to the item scan rather than guessing.
    const handleUnitResolved = async (unit: ResolvedUnit) => {
        const session: ToteSession = {
            unitType: unit.unitType, tote_id: unit.tote_id, label: unit.label, orders: unit.orders,
            totes: unit.totes && unit.totes.length > 1 ? unit.totes : undefined,
        };
        // The unit goes to the account BEFORE the order is claimed — the
        // server refuses a unit change once an order is held.
        if (!(await syncSession({ unit: session }))) return;
        updateTote(session);
        if (unit.unitType !== "light_tote" && unit.orders.length === 1) {
            const outcome = await openOrder(unit.orders[0], null, unit.packState);
            if (outcome === "blocked" || outcome === "failed") {
                // Nothing to scan an item against: back to the tote scan.
                updateTote(null);
                syncUnit(null);
                setView("scan-tote");
            }
            return;
        }
        setView("scan-item");
    };

    // Drop an order from the open tote once it's shipped; when the tote is
    // emptied, go back to the tote scan.
    const advanceTote = (shippedOrderId: string) => {
        const prev = toteRef.current;
        if (!prev) { setView("scan-tote"); return; }
        const remaining = prev.orders.filter(o => o.order_id !== shippedOrderId);
        if (remaining.length === 0) {
            updateTote(null);
            syncUnit(null);
            setView("scan-tote");
            return;
        }
        const next = { ...prev, orders: remaining };
        updateTote(next);
        syncUnit(next);
        setView("scan-item");
    };

    // Open an order on the pack screen. scannedSku is the item scan that
    // identified the order (Single totes) and lands pre-scanned into Box 1;
    // null means the order was identified by its UNIT scan alone (Large
    // totes / P&S bay slots hold exactly one order) — every item starts
    // pending and the operator scans them at the desk as usual.
    //
    // packState is the reopen case: the order was PACKED and its parcels
    // recorded what went into which box, so the desk comes back with the
    // boxes and their units rather than empty. Only a shipping-label reopen
    // supplies one.
    //
    // The order is CLAIMED on the account first (POST open-order). Its answer
    // decides what the desk shows: the saved progress if the order has any
    // (server wins), else the reopen snapshot, else a fresh order.
    type OpenOutcome = "opened" | "blocked" | "resynced" | "failed";
    const openOrder = async (
        order: DispatchOrder,
        scannedSku: string | null,
        packState?: FlaggedPackState | null,
    ): Promise<OpenOutcome> => {
        if (openingRef.current) return "failed";
        openingRef.current = true;
        try {
            // The notes load alongside the claim so they add no wait of their
            // own; a failure resolves to none and the order opens as before.
            const notesP = fetchPackerNotes(order.order_id);
            const claim = await claimOrderOnServer(order, packState as DeskPackState | null | undefined);
            if (claim.kind === "resynced") return "resynced";
            if (claim.kind === "blocked") {
                if (claim.notDispatch) {
                    // Gone from dispatch: take it out of the tote so it stops matching scans.
                    const prev = toteRef.current;
                    if (prev && prev.orders.some(o => o.order_id === order.order_id)) advanceTote(order.order_id);
                }
                return "blocked";
            }
            const serverProgress = claim.kind === "bound" ? claim.packState : null;
            const snapshot = serverProgress ?? packState ?? null;
            // A saved state replaces the fresh one outright — the identifying
            // scan can't have marked anything new (it would double-count).
            const restored = restoreOrderState(order, snapshot);
            const fresh = freshOrderState(order, scannedSku);
            const markedSku = restored ? null : fresh.markedSku;
            const next = restored
                ? { order: restored.order, boxes: restored.boxes, activeBox: serverProgress ? restored.activeBox : undefined }
                : { order: fresh.order, boxes: fresh.boxes, activeBox: undefined };
            const notes = restored ? [] : await notesP;
            applyOrder(next);
            setPackerNotes(notes);
            if (claim.kind === "bound") {
                saverRef.current!.begin(order.order_id, claim.version);
                // A restored state is already on the server; a fresh one still has to go up.
                lastScheduledRef.current = restored && serverProgress
                    ? packStateKey(buildPackState(next.order, next.boxes, next.activeBox), "order-pack")
                    : null;
            } else {
                saverRef.current!.cancel();
                lastScheduledRef.current = null;
            }
            lastBoxSigRef.current = "";
            setCourierResult(null);
            setPriorBooking(null);
            // A freshly scanned order arms auto-book again — the suppress flag
            // is only for reviewing a restored/reopened order, and it would
            // otherwise stay sticky after an abandoned one (goBack never
            // resets it) and silently disable auto-book.
            // A restored order suppresses it for the same reason the flagged
            // Resolve screen does: it can arrive already complete, and
            // auto-booking would re-ship it before the operator had looked.
            setReturnedFromShip(restored !== null);
            // Only count a unit towards the desk's items-scanned stat when the
            // scan actually resolved to a line.
            if (markedSku !== null) bumpItemsScanned(1);

            // The packing window opens here and closes when the labels book.
            cycleRef.current.begin();

            // Audit: one row per order opened at a desk, carrying the picking
            // mode that produced the unit — the anchor row for "what kind of
            // order was this desk packing".
            const tote = toteRef.current;
            logDispatchEvent({
                event: "order-open",
                order_id: order.order_id,
                barcode: scannedSku ?? undefined,
                tote_id: tote?.tote_id,
                pick_type: tote?.unitType === "light_tote" ? "light"
                    : tote?.unitType === "large_tote" ? "large"
                    : tote?.unitType === "pas_location" ? "pick_and_sort"
                    : tote?.unitType === "zone_tote" ? "sorter_2"
                    : undefined,
                desk_id: deskRef.current?.deskId,
                opened_by: scannedSku !== null ? "item-scan" : "unit-scan",
                // Which line the identifying scan landed on. Absent on an
                // item-scan open means the barcode resolved the order but not a
                // line — the operator will have to scan the unit again, which is
                // the signature of a barcode-mapping gap.
                sku: markedSku ?? undefined,
            });

            // Always land on the pack screen — even a fully-scanned single-item
            // order still needs a box chosen (recommendation + box scan), and
            // the pack screen auto-books the courier once a real box is
            // assigned. Products flagged ship_in_packaging are NOT pre-boxed
            // any more (2026-09-09): the flag only highlights the row's
            // own-packaging button, and the operator makes the call.
            // Flagged Picks (2026-10-08): an order with a unit being fetched
            // from high racking opens on the notice first — the shelf to collect
            // from, or the cue to flag while the unit is still on its way. A
            // restored / reopened order was already read once; it goes straight
            // to the pack screen.
            // Data storage (DSD) orders (2026-10-27) open on their own notice
            // before either: leave in original packaging, scan an item to go on.
            // Order notes (2026-10-30) open the same way when an admin left
            // one — on the DSD notice itself when the order is DSD too.
            setView(restored !== null ? "order-pack"
                : isDsdOrder(next.order) ? "dsd-notice"
                : notes.length > 0 ? "order-notes-notice"
                : flaggedPickItems(next.order).length > 0 ? "flagged-pick-notice"
                : "order-pack");
            return "opened";
        } finally {
            openingRef.current = false;
        }
    };

    const handleOrderFound = (order: DispatchOrder, scannedSku: string) => openOrder(order, scannedSku);

    // Server-side reversal of a fulfilment: delete the recorded parcels,
    // void the consignment at the carrier, retract any partner shipment push
    // that hasn't gone out yet, and put the order back to 'dispatch'. Without
    // it the old labels stay recorded and re-shipping appends a second set of
    // parcels.
    //
    // Shared by both routes into a reopen — the title-bar button (the order
    // this desk just shipped) and a scanned shipping label (an order that
    // already left the desk) — so the carrier's actual answer is reported the
    // same way whichever one the operator used.
    //
    // Resolves the reopen result when the order is back at 'dispatch' and
    // safe to pack, or null when it isn't. The result carries the BE's pack
    // snapshot — the boxes and item scans the deleted parcels recorded —
    // which the label-scan route replays onto the desk. The title-bar route
    // ignores it: that order never left this browser, so its live boxes are
    // richer than anything rebuilt from the parcels.
    const reopenOrderOnServer = useCallback(async (orderId: string): Promise<ReopenResult | null> => {
        let packState: FlaggedPackState | null = null;
        try {
            const res = await apiFetch(`/orders/${encodeURIComponent(orderId)}/reopen`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({}),
            });
            // 409 order_busy = another reopen of this order is still
            // running. NOT safe to continue: the order is still packed.
            if (res.status === 409) {
                const busy = await res.clone().json().catch(() => ({} as Record<string, unknown>));
                if (busy?.code === "order_busy") {
                    playError();
                    toast.dismiss();
                    toast.error(t("reopen_busy"), { toastId: "reopen-err" });
                    return null;
                }
            }
            // Any other 409 = not in a reopenable state (e.g. already
            // reopened elsewhere) — nothing to reverse, safe to continue.
            if (!res.ok && res.status !== 409) {
                playError();
                toast.dismiss();
                toast.error(t("reopen_failed"), { toastId: "reopen-err" });
                return null;
            }
            if (res.ok) {
                const result = await res.json().catch(() => ({} as Record<string, unknown>));
                packState = (result?.pack_state as FlaggedPackState | null) ?? null;
                const notified = result?.partner_already_notified;
                if (Array.isArray(notified) && notified.length > 0) {
                    // The sales channel already received the old tracking —
                    // we can't retract it (no partner cancel API); corrected
                    // parcels go out as new pushes on re-ship.
                    toast.warn(t("reopen_partner_notified"), { toastId: "reopen-partner", autoClose: 8000 });
                }
                // The BE voids the old consignment at APC synchronously,
                // so this reports what the carrier actually said. A label
                // that could NOT be voided is still live — the packer has
                // to know before they put a second one on the same parcel.
                const voids = summariseCarrierVoids(result as CarrierVoidResponse);
                // Named by the BE ("APC", "DPD Poland") — the toast used
                // to say APC whatever carrier booked the order.
                const carrier = voids.carrier ?? t("carrier_name_unknown");
                if (voids.failed.length > 0) {
                    playError();
                    toast.error(
                        t("carrier_void_failed_live", {
                            carrier,
                            n: String(voids.failed.length),
                            detail: carrierVoidFailureDetail(voids.failed),
                        }),
                        { toastId: "reopen-carrier-void-failed", autoClose: false },
                    );
                } else if (voids.retrying.length > 0) {
                    toast.warn(
                        t("carrier_void_retrying", { carrier, n: String(voids.retrying.length) }),
                        { toastId: "reopen-carrier-void-retry", autoClose: 15000 },
                    );
                } else if (voids.confirmed > 0) {
                    // A carrier with no cancel API (DPD): nothing was asked
                    // of them — the old label is void in Portal only and the
                    // packer must not hand it over.
                    toast.info(
                        t(voids.localVoid ? "reopen_carrier_void_local" : "reopen_carrier_cancel_done", {
                            carrier,
                            n: String(voids.confirmed),
                        }),
                        { toastId: "reopen-carrier-cancel", autoClose: 8000 },
                    );
                }
                // Shared (add-box) consignment: the carrier renumbered the
                // orders still on it, so their printed labels are dead.
                if (voids.reissued.length > 0) {
                    toast.warn(
                        t("carrier_labels_reissued", {
                            carrier,
                            n: String(voids.reissued.length),
                            detail: carrierReissueDetail(voids.reissued),
                        }),
                        { toastId: "reopen-carrier-reissued", autoClose: false },
                    );
                }
            }
        } catch {
            playError();
            toast.dismiss();
            toast.error(t("reopen_failed"), { toastId: "reopen-err" });
            return null;
        }
        return { packState };
    }, [t]);

    const handleReopenLastOrder = async (data: { order: DispatchOrder; boxes: DispatchBox[] }) => {
        if (!(await reopenOrderOnServer(data.order.order_id))) return;
        // The reopened order is claimed through open-order, with what the
        // desk holds as the snapshot (the parcels that held it are gone).
        const claim = await claimOrderOnServer(data.order, buildPackState(data.order, data.boxes));
        if (claim.kind === "blocked" || claim.kind === "resynced") return;
        // Keep items in their boxes — only clear courier result so new labels are generated
        applyOrder({ order: data.order, boxes: data.boxes });
        if (claim.kind === "bound") {
            saverRef.current!.begin(data.order.order_id, claim.version);
            lastScheduledRef.current = null;
        } else {
            saverRef.current!.cancel();
        }
        setCourierResult(null);
        setPriorBooking(null);
        setReturnedFromShip(true); // suppress auto-book, let user review first
        setView("order-pack");
    };

    const handlePackComplete = (result: CourierBookingResult, updatedBoxes: DispatchBox[]) => {
        setCourierResult(result);
        setPriorBooking(null);
        setBoxes(updatedBoxes);
        // Labels are printed automatically by the courier API response — go straight to confirm
        setView("confirm-ship");
        void saverRef.current?.flush();
    };

    const handleShipComplete = () => {
        bumpOrdersShipped();
        cycleRef.current.complete();
        if (currentOrder) {
            setLastOrder({ order: currentOrder, boxes });
            saveLastShippedOrder(currentOrder, boxes);
            // Status mutation happens server-side: dispatch-close-order
            // POSTs the parcel list to /orders/:id/labels which sets
            // status='fulfilled' + packed_at/by, AND raises the flag itself
            // when the pack shipped short.
            //
            // The desk used to fire that flag from here, off its own item
            // counts. It could not tell a shortfall the operator just
            // marked from one restored by a reopen and already resolved by
            // a leader, so every label re-print on a resolved order
            // re-flagged it (bargainfox:337295). The BE compares the
            // shortfall before and after the pack in the same transaction —
            // nothing to do here.
        }
        const shippedId = currentOrder?.order_id;
        // The server cleared the claim and the progress when it recorded the
        // labels; stop the queue so a late save can't read as "released".
        saverRef.current?.cancel();
        lastScheduledRef.current = null;
        applyOrder(null);
        setCourierResult(null);
        setPriorBooking(null);
        clearSessionOrder();
        // Work through the remaining orders in the open tote; the tote scan
        // returns only once they're all shipped.
        if (shippedId && toteRef.current) advanceTote(shippedId);
        else setView(toteRef.current ? "scan-item" : "scan-tote");
    };

    // Opens the flag-confirm view where the user sees label count, +/-, and
    // confirms. `reason` tags the source (packing vs label issue).
    // `returnTo` controls where the back button goes if the user cancels.
    const requestFlag = (reason: string, returnTo: DispatchView) => {
        if (!currentOrder) return;
        setPendingFlagReason(reason);
        setFlagLabelCount(boxes.length || 1);
        setFlagReturnView(returnTo);
        setView("flag-confirm");
        void saverRef.current?.flush();
    };

    // Flag from the packing/scanning screen — "Packing Issue"
    const handleFlagOrder = () => requestFlag(t("flagged_reason_packing"), "order-pack");

    // Flag from the Confirm Shipment screen — "Label Issue"
    const handleFlagOrderLabel = () => requestFlag(t("flagged_reason_label"), "confirm-ship");

    // Called when the user confirms the flag — POST to /orders/:id/flag
    // to allocate a fresh FLG-YYMMDD-XXXX ID server-side, then print
    // labels using that ID. The BE flips the order's status='flagged' as
    // part of the same transaction.
    const confirmFlagOrder = async () => {
        if (!currentOrder || flagPrinting) return;
        setFlagPrinting(true);
        // The saved desk must not be behind the flag.
        await flushNow();

        let flaggedId: string | null = null;
        try {
            const res = await apiFetch(`/orders/${encodeURIComponent(currentOrder.order_id)}/flag`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    reason: pendingFlagReason,
                    label_count: flagLabelCount,
                    flagged_by_uid: getUserId(),
                    flagged_by_name: getCurrentUserName(),
                    // The pack desk at flag time, so the leader who resolves it
                    // opens the order exactly as the packer left it.
                    pack_state: buildPackState(currentOrder, boxes),
                }),
            });
            if (res.ok) {
                const data = await res.json();
                flaggedId = data?.flagged_id ?? null;
            }
        } catch { /* fall through; without an ID we can't print */ }

        if (!flaggedId) {
            setFlagPrinting(false);
            toast.error(t("server_error"), { toastId: "flag-server-err" });
            return;
        }

        // Print labels — saved network printer first (backend rejects the job
        // if its agent is offline, so a dead selection falls through cleanly),
        // then the local agent, then the browser print dialog.
        const html = buildFlaggedLabels({
            flaggedId,
            orderNumber: currentOrder.order_number,
            reason: pendingFlagReason,
            labelCount: flagLabelCount,
        });
        // Honour the device's explicit "Local" choice — never route flag
        // labels to a remote printer the operator didn't pick.
        const savedPrinter = getSavedNetworkPrinter();
        let result: { success: boolean; error?: string } = { success: false };
        if (savedPrinter && getPrintModePreference() !== "local") {
            result = await submitNetworkPrintJob(savedPrinter.agentId, savedPrinter.printerName, html, 1);
        }
        if (!result.success) {
            result = await silentPrint(html, 1, getSavedLocalPrinter() ?? undefined);
        }
        if (!result.success) {
            result = await browserPrint(html);
        }

        // The server cleared the claim and the progress with the flag.
        saverRef.current?.cancel();
        lastScheduledRef.current = null;
        setFlagPrinting(false);
        playError();
        setFlaggedCount(1);
        setView("flagged");
        const flaggedOrderId = currentOrder.order_id;
        // A flagged order is the desk's last finished order too, so the
        // title-bar reopen button must point at it. Left on the previously
        // shipped order, a packer undoing a mistaken flag reopened THAT
        // order instead and voided its labels. The BE reopen accepts
        // 'flagged' (REOPENABLE_STATUSES) and puts it back to 'dispatch'.
        setLastOrder({ order: currentOrder, boxes });
        saveLastShippedOrder(currentOrder, boxes);
        setTimeout(() => {
            applyOrder(null);
            setCourierResult(null);
            setPriorBooking(null);
            clearSessionOrder();
            // Drop the flagged order from the open tote — same as shipping
            // does. Without this the flagged order stayed in the tote's
            // order list and kept matching item scans, so the operator
            // couldn't open the tote's next order.
            if (toteRef.current) advanceTote(flaggedOrderId);
            else setView("scan-tote");
        }, 1200);
    };

    // Singles tote › Flag missing (2026-10-28): the item scan is the only way
    // into an order on a Single tote, so an order whose unit is not in the
    // tote could never be opened or flagged. This flags each order still in
    // the tote with every unit marked missing — the same POST /orders/:id/flag
    // and snapshot the pack screen sends after marking units missing — one
    // order at a time. No flag labels: there is nothing in hand to label.
    // only_if_at_dispatch: the list is from the tote scan, so the server
    // refuses any order another desk has shipped, flagged or opened since.
    const confirmFlagRemaining = async (orders: DispatchOrder[]) => {
        if (flagRemainingBusy || orders.length === 0) return;
        setFlagRemainingBusy(true);
        const reason = t("flagged_reason_missing_dispatch");
        // Orders that left the tote either way: flagged now, or no longer
        // at dispatch (shipped / flagged elsewhere).
        const gone: string[] = [];
        let flagged = 0;
        let failed = 0;
        for (const o of orders) {
            try {
                const res = await apiFetch(`/orders/${encodeURIComponent(o.order_id)}/flag`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        reason,
                        label_count: 1,
                        flagged_by_uid: getUserId(),
                        flagged_by_name: getCurrentUserName(),
                        pack_state: allMissingPackState(o),
                        only_if_at_dispatch: true,
                    }),
                });
                const data = await res.json().catch(() => null);
                if (res.ok && data?.flagged_id) {
                    flagged += 1;
                    gone.push(o.order_id);
                } else if (data?.reason === "not_at_dispatch") {
                    gone.push(o.order_id);
                } else {
                    failed += 1;
                }
                logDispatchEvent({
                    event: "flag-missing", order_id: o.order_id, desk_id: deskInfo?.deskId,
                    result: res.ok ? "ok" : `fail-${data?.reason ?? res.status}`,
                });
            } catch {
                failed += 1;
            }
        }
        setFlagRemainingBusy(false);

        if (failed > 0) {
            toast.error(t("flag_remaining_failed", { count: String(failed) }), { toastId: "flag-remaining-err" });
        }
        const prev = toteRef.current;
        const remaining = prev ? prev.orders.filter(o => !gone.includes(o.order_id)) : [];
        if (gone.length > 0) {
            if (remaining.length === 0) {
                updateTote(null);
                syncUnit(null);
            } else if (prev) {
                const next = { ...prev, orders: remaining };
                updateTote(next);
                syncUnit(next);
            }
        }
        const after: DispatchView = toteRef.current ? "scan-item" : "scan-tote";
        if (flagged === 0) {
            setView(after);
            return;
        }
        playError();
        setFlaggedCount(flagged);
        setView("flagged");
        setTimeout(() => setView(after), 1200);
    };

    const handleBackToHome = () => {
        navigate("/dispatch/menu");
    };

    // "Change desk" / "Change tote" are refused while an order is open (the
    // server refuses too): the packer ships, flags or puts the order back first.
    const handleChangeDesk = async () => {
        if (orderRef.current) return;
        cycleRef.current.end();
        if (!(await syncSession({ desk_id: null, unit: null }))) return;
        updateTote(null);
        updateDesk(null);
        setView("scan-desk");
    };

    // Go back from a tote's order to the tote scan (changing tote).
    const handleChangeTote = async () => {
        if (orderRef.current) return;
        cycleRef.current.end();
        if (!(await syncSession({ unit: null }))) return;
        updateTote(null);
        setView("scan-tote");
    };

    // Put back: the claim is released, the order stays at dispatch and its
    // boxes and scans are kept for whoever opens it next. The latest desk is
    // saved first.
    const handleBackToScanItem = async () => {
        if (leavingRef.current) return;
        leavingRef.current = true;
        try {
            const orderId = orderRef.current?.order_id;
            if (orderId && saverRef.current?.isBound()) {
                await flushNow();
                saverRef.current.cancel();
                await withTimeout(deskApi.releaseOrder(orderId), 4000);
            } else {
                saverRef.current?.cancel();
            }
            lastScheduledRef.current = null;
            applyOrder(null);
            setCourierResult(null);
            setPriorBooking(null);
            clearSessionOrder();
            setView(toteRef.current ? "scan-item" : "scan-tote");
        } finally {
            leavingRef.current = false;
        }
    };

    // A shelved flagged-pick unit scanned on the notice (2026-10-09): the
    // same state change as a pack-screen item scan — one more unit on the
    // line, into box 1 — so the pack screen opens with it ticked and the
    // packer never scans it twice. Mirrors openOrder's identifying-scan
    // mark and the pack screen's scan handler (dispatch-order-pack.tsx).
    const handleCollectFlaggedPick = (item: DispatchOrderItem) => {
        if (!currentOrder) return;
        const idx = currentOrder.items.findIndex(i => i.item_id === item.item_id);
        if (idx < 0) return;
        const cur = currentOrder.items[idx];
        const scannedCount = (cur.scannedCount || 0) + 1;
        const resolved = scannedCount + (cur.missingCount || 0) + (cur.damagedCount || 0)
            + (cur.wrongItemCount || 0) + (cur.barcodeDamagedCount || 0);
        const updated: DispatchOrderItem = {
            ...cur,
            scannedCount,
            assignedBoxes: [...(Array.isArray(cur.assignedBoxes) ? cur.assignedBoxes : []), 1],
            scanStatus: resolved >= cur.quantity ? "scanned" : "pending",
        };
        const items = [...currentOrder.items];
        items[idx] = updated;
        setCurrentOrder({ ...currentOrder, items });
        setBoxes(prev => {
            const list = prev.length > 0 ? prev : [{ id: 1, items: [] }];
            return list.map(b => b.id === 1 ? { ...b, items: [...b.items, cur.sku] } : b);
        });
        bumpItemsScanned(1);
        logDispatchEvent({ event: "pack-item-scan", barcode: item.barcode, sku: cur.sku, order_id: currentOrder.order_id, desk_id: deskInfo?.deskId, box: 1, result: "ok" });
    };

    // The scan that clears the DSD notice is a pack scan when its line still
    // has a unit to scan; one already counted (the identifying scan of a
    // one-unit line) just moves on. Then whatever opening would have shown.
    // The order-notes notice clears the same way.
    const handleDsdNoticeScan = (item: DispatchOrderItem) => {
        if (!currentOrder) return;
        if (hasUnitLeft(item)) handleCollectFlaggedPick(item);
        setView(flaggedPickItems(currentOrder).length > 0 ? "flagged-pick-notice" : "order-pack");
    };

    let content: React.ReactNode = null;

    // The desk session is being fetched from the account.
    if (!sessionReady) {
        return (
            <div className="scan-panel dispatch-scan">
                <div className="scan-centre">
                    <FlowLoader message={t("loading")} />
                </div>
            </div>
        );
    }

    if (view === "scan-desk") {
        content = <DispatchScanDesk onDeskScanned={handleDeskScanned} goBack={handleBackToHome} />;
    } else if (view === "scan-tote" && deskInfo) {
        content = <DispatchScanTote deskInfo={deskInfo} onUnitResolved={handleUnitResolved} goBack={handleBackToHome} onChangeDesk={currentOrder ? undefined : handleChangeDesk} autoPrint={autoPrint} onToggleAutoPrint={toggleAutoPrint} lastOrder={lastOrder} onReopenOrder={handleReopenLastOrder} onReopenShippedOrder={reopenOrderOnServer} />;
    } else if (view === "scan-item" && deskInfo) {
        content = <DispatchScanItem deskInfo={deskInfo} onOrderFound={handleOrderFound} goBack={handleChangeTote} onChangeDesk={currentOrder ? undefined : handleChangeDesk} autoPrint={autoPrint} onToggleAutoPrint={toggleAutoPrint} lastOrder={lastOrder} onReopenOrder={handleReopenLastOrder} toteOrders={toteSession?.orders ?? []} toteLabel={toteSession?.label || toteSession?.tote_id} onFlagRemaining={() => setView("flag-remaining")} />;
    } else if (view === "flag-remaining" && deskInfo) {
        content = (
            <DispatchFlagRemaining
                deskInfo={deskInfo}
                toteLabel={toteSession?.label || toteSession?.tote_id}
                orders={toteSession?.orders ?? []}
                busy={flagRemainingBusy}
                onCancel={() => setView(toteRef.current ? "scan-item" : "scan-tote")}
                onConfirm={confirmFlagRemaining}
            />
        );
    } else if (view === "order-pack" && currentOrder && deskInfo) {
        content = (
            <DispatchOrderPack
                order={currentOrder}
                boxes={boxes}
                deskInfo={deskInfo}
                unitTotes={toteSession?.totes}
                suppressAutoModal={returnedFromShip}
                liveBooking={priorBooking}
                onUpdateOrder={setCurrentOrder}
                onUpdateBoxes={setBoxes}
                onComplete={(result, updatedBoxes) => { setReturnedFromShip(false); handlePackComplete(result, updatedBoxes); }}
                onItemScanned={bumpItemsScanned}
                onFlagOrder={handleFlagOrder}
                goBack={handleBackToScanItem}
                initialActiveBox={activeBox}
                onActiveBoxChange={setActiveBox}
                onBeforeBook={flushNow}
                onOrderReset={() => { flushNextRef.current = true; }}
            />
        );
    } else if (view === "flagged-pick-notice" && currentOrder) {
        content = (
            <FlaggedPickNotice
                order={currentOrder}
                onContinue={() => setView("order-pack")}
                onFlag={() => requestFlag(AWAITING_FLAGGED_PICK_REASON, "flagged-pick-notice")}
                onCollect={handleCollectFlaggedPick}
                onLeave={handleBackToScanItem}
            />
        );
    } else if (view === "dsd-notice" && currentOrder) {
        content = (
            <DsdOrderNotice
                order={currentOrder}
                notes={packerNotes}
                onScanned={handleDsdNoticeScan}
                onLeave={handleBackToScanItem}
            />
        );
    } else if (view === "order-notes-notice" && currentOrder) {
        content = (
            <OrderNotesNotice
                order={currentOrder}
                notes={packerNotes}
                onScanned={handleDsdNoticeScan}
                onLeave={handleBackToScanItem}
            />
        );
    } else if (view === "flag-confirm" && currentOrder) {
        // The order being flagged, in the same bar the pack screen showed,
        // over a centred card: reason, how many flag labels to print, then
        // Cancel beside Flag & print (it used to be an X in the corner).
        const unitsResolved = currentOrder.items.reduce((sum, i) =>
            sum + (i.scannedCount || 0) + (i.missingCount || 0) + (i.damagedCount || 0)
            + (i.wrongItemCount || 0) + (i.barcodeDamagedCount || 0), 0);
        const unitsTotal = currentOrder.items.reduce((sum, i) => sum + i.quantity, 0);
        const shippedWeightG = currentOrder.items.reduce((sum, i) =>
            sum + i.weight_grams * ((i.scannedCount || 0) + (i.barcodeDamagedCount || 0)), 0);
        content = (
            <div className="dispatch-page dispatch-bench dispatch-flag-screen">
                <DispatchOrderBar
                    order={currentOrder}
                    done={unitsResolved}
                    total={unitsTotal}
                    weightKg={shippedWeightG / 1000}
                />
                <div className="dispatch-flag-stage">
                    <div className="dispatch-flag-confirm-card">
                        <div className="dispatch-flag-confirm-head">
                            <div className="dispatch-flag-confirm-icon">
                                <Flag size={28} aria-hidden="true" />
                            </div>
                            <h2 className="dispatch-flag-confirm-title">{t("flag_order")}</h2>
                            <p className="dispatch-flag-confirm-reason">{pendingFlagReason}</p>
                        </div>

                        <div className="dispatch-flag-confirm-labels">
                            <span className="dispatch-flag-confirm-labels-title">{t("flag_labels_to_print")}</span>
                            <div className="dispatch-flag-label-count">
                                <button
                                    type="button"
                                    className="dispatch-flag-count-btn"
                                    disabled={flagLabelCount <= 1}
                                    onClick={() => setFlagLabelCount(c => Math.max(1, c - 1))}
                                    aria-label="-"
                                >
                                    <Minus size={26} />
                                </button>
                                <span className="dispatch-flag-count-value">{flagLabelCount}</span>
                                <button
                                    type="button"
                                    className="dispatch-flag-count-btn"
                                    onClick={() => setFlagLabelCount(c => c + 1)}
                                    aria-label="+"
                                >
                                    <Plus size={26} />
                                </button>
                            </div>
                            <p className="dispatch-flag-confirm-hint">{t("flag_label_apply_hint")}</p>
                        </div>

                        <div className="dispatch-flag-confirm-actions">
                            <button type="button" className="btn btn-ghost" onClick={() => setView(flagReturnView)}>
                                {t("cancel")}
                            </button>
                            <button
                                type="button"
                                className="btn btn-danger"
                                onClick={confirmFlagOrder}
                                disabled={flagPrinting}
                            >
                                <Printer size={22} />
                                {flagPrinting ? t("printing") : t("flag_and_print")}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    } else if (view === "flagged") {
        content = (
            <div className="dispatch-flag-overlay">
                <Flag strokeWidth={1.5} />
                <h1>{flaggedCount > 1 ? t("orders_flagged", { count: String(flaggedCount) }) : t("order_flagged")}</h1>
            </div>
        );
    } else if (view === "confirm-ship" && currentOrder && courierResult && deskInfo) {
        content = (
            <DispatchCloseOrder
                order={currentOrder}
                courierResult={courierResult}
                deskInfo={deskInfo}
                boxes={boxes}
                autoPrint={autoPrint}
                onShipComplete={handleShipComplete}
                onBeforeShip={flushNow}
                onFlagOrder={handleFlagOrderLabel}
                goBack={() => {
                    // Remember the label that is already booked, so the pack
                    // screen can warn if the packer changes the box count.
                    setPriorBooking(courierResult.booking_id
                        ? { id: courierResult.booking_id, pieces: courierResult.parcels.length, carrierName: courierResult.carrier }
                        : null);
                    setCourierResult(null);
                    setReturnedFromShip(true);
                    setView("order-pack");
                }}
            />
        );
    }

    return (
        <DispatchSessionProvider stats={stats} cycle={cycle}>
            {content}

            {/* The session counts and points pill now sit in each screen's
                own bars; only the time-bonus popup needs a fixed layer. */}
            <DispatchTimeBonus cycle={cycle} />

            {/* Idle alert overlay */}
            {showIdleAlert && (
                <div className="dispatch-idle-overlay" onClick={() => setShowIdleAlert(false)}>
                    <div className="dispatch-idle-card" onClick={e => e.stopPropagation()}>
                        <div className="dispatch-idle-icon-row">
                            <div className="dispatch-idle-icon-circle dispatch-idle-icon-pulse">
                                <ScanBarcode size={28} />
                            </div>
                            <div className="dispatch-idle-icon-dots">
                                <span /><span /><span />
                            </div>
                            <div className="dispatch-idle-icon-circle dispatch-idle-icon-muted">
                                <Package size={28} />
                            </div>
                        </div>
                        <p className="dispatch-idle-text">{t("idle_alert")}</p>
                        <button className="btn btn-primary btn-xl btn-full" onClick={() => setShowIdleAlert(false)}>
                            <Hand size={22} />
                            {t("im_here")}
                        </button>
                    </div>
                </div>
            )}
        </DispatchSessionProvider>
    );
};

export default Dispatch;
